import React, { useState, useCallback, useEffect, Suspense, lazy } from 'react';
import {
  Search,
  BookOpen,
  LayoutDashboard,
  Users,
  Calendar,
  FileText,
  CalendarDays,
  Receipt,
  Banknote,
  ClipboardList,
  UserCheck,
  UserCog,
  FlaskConical,
  Package,
  BarChart3,
  Stethoscope,
  CheckSquare,
  Zap,
  Settings,
  ChevronRight,
  ChevronDown,
  Bell,
  User,
  LogOut,

  Menu,
  MapPin,
  X,
  ListOrdered,
  Monitor,
  Ticket,
  ExternalLink,
  Syringe, Pill,
  Wrench,
  Truck,
  ShoppingCart,
  ClipboardCheck,
  Shield,
  Briefcase,
  Heart,
  Activity,
  Wallet,
  PackagePlus,
  Tag as TagIcon,
  ScrollText,
  FileX,
  UserX,
  MessageSquare,
  MessageSquareWarning,
  Clock,
  Stethoscope as StethoscopeIcon,
  Award,
  Image as ImageIcon
} from 'lucide-react';
import Dashboard from './components/Dashboard';
import ServerDownScreen from './components/ServerDownScreen';
import PatientManagement from './components/PatientManagement';
import TokenManagement from './components/TokenManagement';
import NurseStation from './components/NurseStation';
import InvoiceManagement from './components/InvoiceManagement';
import { useToast } from './components/UIProvider';
import { CashDrawerProvider } from './components/cashDrawer/CashDrawerContext';
import CashDrawerButton from './components/cashDrawer/CashDrawerButton';
import MyAttendance from './components/attendance/MyAttendance';
import AttendanceBoard from './components/attendance/AttendanceBoard';
import AttendanceSettings from './components/attendance/AttendanceSettings';
import ComplaintsManagement from './components/complaints/ComplaintsManagement';
import PunchInReminder from './components/attendance/PunchInReminder';
import PunchOutReminder from './components/attendance/PunchOutReminder';
import OrderDayReminder from './components/orders/OrderDayReminder';
import ExpenseManagement from './components/expenses/ExpenseManagement';
import FamilyManagement from './components/FamilyManagement';
import UserManagement from './components/UserManagement';
import VendorsManagement from './components/VendorsManagement';
import MedicalServicesManagement from './components/MedicalServicesManagement';
import TagsManagement from './components/TagsManagement';
import ClinicalSettingsManagement from './components/ClinicalSettingsManagement';
import DoctorSettingsManagement from './components/DoctorSettingsManagement';
import TimezoneSettings from './components/TimezoneSettings';
import BrandingSettings from './components/BrandingSettings';
import MedicinesManagement from './components/MedicinesManagement';
import AssetsManagement from './components/AssetsManagement';
import MedicineOrdersManagement from './components/MedicineOrdersManagement';
import OrderReviewsManagement from './components/orders/OrderReviewsManagement';
import StockReceiptsManagement from './components/StockReceiptsManagement';
import PrescriptionManagement from './components/PrescriptionManagement';
import RolesPermissions from './components/RolesPermissions';
import ReleaseNotes from './components/ReleaseNotes';
import ReportsManagement from './components/reports/ReportsManagement';
import DeletedDocumentsManagement from './components/DeletedDocumentsManagement';
import DeletedPatientsManagement from './components/DeletedPatientsManagement';
import PatientInstructionsManagement from './components/PatientInstructionsManagement';
import SpecialitiesManagement from './components/SpecialitiesManagement';
import IntakeQuestionsManagement from './components/IntakeQuestionsManagement';
import ClinicLogo from './components/ClinicLogo';
import Login from './components/Login';
import ProfileMenu from './components/ProfileMenu';
import NewVersionBanner from './components/NewVersionBanner';
import EnvironmentStrip, { ENVIRONMENT_STRIP_OFFSET, ENVIRONMENT_STRIP_TOP } from './components/EnvironmentStrip';
import useTheme from './hooks/useTheme';
import useColorTheme from './hooks/useColorTheme';
import { rolePermissionAPI, userAPI, clinicalSettingsAPI, locationAPI, deletedDocumentAPI, complaintAPI, orderReviewAPI, fetchEnvironment } from './services/api';
import { setClinicTimezone } from './utils/clinicTime';
import { HIDDEN_MENU_IDS } from './config/features';
import './App.css';
import { updatedGuideIds } from './utils/helpSeen';

// The guides carry 50-odd topics of text and 11 MB of screenshots behind them, so they load when
// somebody opens them rather than riding in the bundle every member of staff downloads to sign in.
const HelpCenter = lazy(() => import('./components/HelpCenter'));

// For menu items whose module has multiple sub-pages all under the same module, this map
// pins each menu ID to the sub-action that controls its visibility. Today this is only
// USER_MANAGEMENT — Users and Roles & Permissions are separate sidebar items but share the
// same module — but the same mechanism extends to any future "multiple sidebar items, one
// module" case. Items not listed fall back to the module's VIEW action.
const MENU_TO_PERMISSION_ACTION = {
  'user-list': 'VIEW_USERS_TAB',
  'roles-permissions': 'VIEW_ROLES_TAB',
  // Display Board and Token Queue both live under QUEUE_MANAGEMENT, so without their own actions
  // granting one granted the other. Note this gates the sidebar LINK only: the board itself is
  // served unauthenticated for the waiting-room TV.
  'display-board': 'VIEW_DISPLAY_BOARD',
  'token-queue': 'VIEW_TOKEN_QUEUE_TAB',
  // Each report group is grantable on its own, so the sidebar hides the groups a role cannot open
  // rather than showing four entries that all lead to a refusal.
  'reports-finance': 'VIEW_FINANCE_REPORTS',
  'reports-opd': 'VIEW_OPD_REPORTS',
  'reports-clinical': 'VIEW_CLINICAL_REPORTS',
  'reports-inventory': 'VIEW_INVENTORY_REPORTS',
  'reports-attendance': 'VIEW_ATTENDANCE_REPORTS',
  // Attendance: everyone has their own page; the board is everyone's attendance and the settings
  // page is the rules, each its own action.
  'attendance-board': 'VIEW_ALL',
  'attendance-settings': 'EDIT',
  // Expenses: each page already hides its own tab on these actions, but the sidebar link did not,
  // so revoking one left a link that opened an empty page. The link now follows the tab.
  'expense-dashboard': 'VIEW_DASHBOARD_TAB',
  'expense-entries': 'VIEW_ENTRIES_TAB',
  'expense-categories': 'VIEW_CATEGORIES_TAB',
  'expense-cheques': 'VIEW_CHEQUES_TAB',
  // Monthly Report, Cash Position and Notes deliberately share the Dashboard's action rather than
  // adding more switches: they are the dashboard's figures and its scratchpad, read by the same people.
  'expense-monthly-report': 'VIEW_DASHBOARD_TAB',
  'expense-cash-position': 'VIEW_DASHBOARD_TAB',
  'expense-notes': 'VIEW_DASHBOARD_TAB',
};

// Maps sidebar menu item IDs to permission module names. Each pharmacy/settings submenu
// now maps to its own permission module so admins can grant Medicines without granting
// Vendors, Medical Services without Tags, etc. The parent menu items ('inventory',
// 'settings') intentionally have no mapping — the sidebar visibility check treats unmapped
// items as "always visible", and the parent only renders when at least one child does.
const MENU_TO_PERMISSION_MODULE = {
  'dashboard': 'DASHBOARD',
  'patients': 'PATIENTS',
  'family': 'FAMILY',
  'nurse-station': 'NURSE_STATION',
  'queue': 'QUEUE_MANAGEMENT',
  'token-queue': 'QUEUE_MANAGEMENT',
  'display-board': 'QUEUE_MANAGEMENT',
  'prescriptions': 'PRESCRIPTIONS',
  'medicines': 'PHARMACY_MEDICINES',
  // Supplies are the same catalogue, so they are governed by the same permission.
  'medical-supplies': 'PHARMACY_MEDICINES',
  'medicine-orders': 'PHARMACY_ORDERS',
  'stock-receipts': 'PHARMACY_STOCK_RECEIPTS',
  'assets': 'PHARMACY_ASSETS',
  'vendors': 'PHARMACY_VENDORS',
  'users': 'USER_MANAGEMENT',
  'user-list': 'USER_MANAGEMENT',
  'roles-permissions': 'USER_MANAGEMENT',
  'invoice': 'INVOICE',
  'expenses': 'EXPENSES',
  'expense-dashboard': 'EXPENSES',
  'expense-entries': 'EXPENSES',
  'expense-categories': 'EXPENSES',
  'expense-cheques': 'EXPENSES',
  'expense-monthly-report': 'EXPENSES',
  'expense-cash-position': 'EXPENSES',
  'expense-notes': 'EXPENSES',
  'attendance-mine': 'ATTENDANCE',
  'attendance-board': 'ATTENDANCE',
  'attendance-settings': 'ATTENDANCE',
  'reports': 'REPORTS',
  'complaints': 'COMPLAINTS',
  'reports-finance': 'REPORTS',
  'reports-opd': 'REPORTS',
  'reports-clinical': 'REPORTS',
  'reports-inventory': 'REPORTS',
  'reports-attendance': 'REPORTS',
  'medical-services': 'SETTINGS_MEDICAL_SERVICES',
  'tags': 'SETTINGS_TAGS',
  'clinical-settings': 'SETTINGS_CLINICAL',
  // Patient Instructions config reuses the Clinical Settings permission.
  'patient-instructions': 'SETTINGS_CLINICAL',
  // Doctor Settings (per-doctor token prefix + room) reuses the Clinical Settings permission
  // rather than introducing a new permission module.
  'doctor-settings': 'SETTINGS_CLINICAL',
  // Speciality catalog scopes the instruction chips + quick lab tests; same clinical settings gate.
  'specialities': 'SETTINGS_CLINICAL',
  // The first-visit intake question catalogue is clinical configuration; same gate again.
  'intake-questions': 'SETTINGS_CLINICAL',
  'timezone-settings': 'SETTINGS_TIMEZONE',
  'branding-settings': 'SETTINGS_BRANDING',
};

function App() {
  // Theme
  const { theme, setTheme } = useTheme();

  // The app rarely speaks for itself, but a complaint arriving while nobody was looking has to be
  // said out loud once per session rather than waiting to be noticed in the sidebar.
  const showToast = useToast();

  // Auth state
  const [authUser, setAuthUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);

  // Colour theme (Blue or Green), remembered per user; see useColorTheme.
  const { colorTheme, setColorTheme } = useColorTheme(authUser?.username);
  const [authLoading, setAuthLoading] = useState(true);

  // Server unreachable — flipped by the 'clinic:server-unreachable' event from api.js.
  const [serverDown, setServerDown] = useState(false);

  // Read the initial module from the URL hash so the page survives refresh.
  // e.g., http://localhost:3000/#/invoice → activeModule = 'invoice'
  const getModuleFromHash = () => {
    const hash = window.location.hash.replace(/^#\/?/, '');
    // Only the first segment names the module. Anything after it is that module's own state, e.g.
    // #/invoice/counter/rx/<id> reopens a patient's bill at the billing counter after a refresh.
    return hash.split('/')[0] || 'dashboard';
  };

  const [activeModule, setActiveModuleRaw] = useState(getModuleFromHash);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Mobile slide-over nav (below lg). `isDesktop` tracks the lg breakpoint so the desktop
  // "collapsed rail" mode (sidebarCollapsed) only applies on large screens — the mobile drawer
  // is always the full-width labelled sidebar.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    () => (typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : true),
  );
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => {
      setIsDesktop(e.matches);
      if (e.matches) setMobileNavOpen(false); // leaving mobile → drop the drawer state
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedMenus, setExpandedMenus] = useState(['queue']); // Queue expanded by default

  // Wrapper: when activeModule changes, sync to URL hash. If the user clicks the
  // sidebar item for the module they're already on, dispatch a "module-reset" event
  // so the active component can scroll back to its landing view (e.g. Invoices: drop
  // the New-Invoice picker and show the list again).
  const setActiveModule = useCallback((moduleId) => {
    setActiveModuleRaw((current) => {
      if (current === moduleId) {
        window.dispatchEvent(new CustomEvent('clinic:module-reset', { detail: { moduleId } }));
      }
      return moduleId;
    });
    window.location.hash = `/${moduleId}`;
  }, []);

  // Listen for browser back/forward navigation
  useEffect(() => {
    const onHashChange = () => {
      const mod = getModuleFromHash();
      setActiveModuleRaw(mod);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // On mount: if the hash points to a submenu child (e.g., #/token-queue), auto-expand the
  // parent so the sidebar shows the active item. This runs once after menuItems are built.
  const autoExpandedRef = React.useRef(false);
  // Ref for the scrollable <main> container — used by the scroll-reset effect below so
  // switching modules always lands at the top of the new page.
  const mainScrollRef = React.useRef(null);
  // Scroll the main content back to the top whenever the user navigates to a different
  // module. Without this, if you scroll down on Users, switch to Patient, then click
  // Users again, you'd land halfway down the page — the <main> element's scrollTop is
  // preserved across the React re-render.
  useEffect(() => {
    if (mainScrollRef.current) {
      mainScrollRef.current.scrollTop = 0;
    }
  }, [activeModule]);
  const [displayBoardWindow, setDisplayBoardWindow] = useState(null);
  const [rolePermissions, setRolePermissions] = useState(null); // { MODULE: { VIEW: bool, CREATE: bool, ... } }
  // Whether this sign-in's permissions have been fetched, whatever the answer. Until then no
  // page opens (see renderModule); a failed fetch still settles it, and the app stays fail-open.
  const [permissionsSettled, setPermissionsSettled] = useState(false);
  // Whether ANY doctor has a nurse taking their vitals. The Nurse Station is a per-doctor setting
  // now (Doctor Settings), so this only decides whether the sidebar entry is worth showing at all —
  // the queue itself naturally contains just the nurse-backed doctors' patients. Defaults to true
  // while loading so the menu doesn't flicker.
  const [nurseStationInUse, setNurseStationInUse] = useState(true);
  // When true, the prescription workspace shows a "Save Template" button. Default false while
  // loading (and when off, templates are created only from the Templates screen).
  const [allowSaveTemplateFromPrescription, setAllowSaveTemplateFromPrescription] = useState(false);

  // Fetch effective permissions for a user (role + user-level overrides)
  const fetchRolePermissions = useCallback(async (role, userId) => {
    try {
      // Try effective permissions first (role + user overrides merged)
      if (userId) {
        try {
          const data = await userAPI.getEffectivePermissions(userId);
          if (data && Object.keys(data).length > 0 && !data.error) {
            setRolePermissions(data);
            return;
          }
        } catch { /* fall through to role-based */ }
      }
      // Fallback: use role permissions
      const data = await rolePermissionAPI.getByRole(role);
      setRolePermissions(data.permissions || null);
    } catch (err) {
      console.error('Failed to fetch permissions:', err);
      setRolePermissions(null);
    } finally {
      setPermissionsSettled(true);
    }
  }, []);

  // QA and the real system look the same; QA says so on every screen, sign-in included.
  const [environment, setEnvironment] = useState(null);
  useEffect(() => {
    let cancelled = false;
    fetchEnvironment().then((env) => { if (!cancelled) setEnvironment(env); });
    return () => { cancelled = true; };
  }, []);
  const isQa = environment === 'qa';
  useEffect(() => {
    if (isQa && !document.title.startsWith('[QA] ')) document.title = '[QA] ' + document.title;
  }, [isQa]);

  // Restore auth from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem('clinic_auth_token');
    const user = localStorage.getItem('clinic_auth_user');
    if (token && user) {
      try {
        const parsed = JSON.parse(user);
        setAuthUser(parsed);
        setAuthToken(token);
        fetchRolePermissions(parsed.role, parsed.id);
      } catch {
        localStorage.removeItem('clinic_auth_token');
        localStorage.removeItem('clinic_auth_user');
      }
    }
    setAuthLoading(false);
  }, [fetchRolePermissions]);

  // Listen for 401 auto-logout events
  useEffect(() => {
    // Hard logout: clear auth state AND every transient state that could keep the user
    // pinned to a dead route (URL hash, active module). api.js already clears localStorage
    // before dispatching this event, but we re-do it here so any caller of auth:logout
    // (not just 401 handler) ends up in the same clean state. Without the hash reset, a
    // refresh could land them back on /prescriptions with no token and an empty page.
    const handleLogout = () => {
      try {
        localStorage.removeItem('clinic_auth_token');
        localStorage.removeItem('clinic_auth_user');
      } catch {}
      setAuthUser(null);
      setAuthToken(null);
      setRolePermissions(null);
      setPermissionsSettled(false);
      setActiveModuleRaw('dashboard');
      if (window.location.hash) {
        // Replace rather than push so the back button doesn't bounce them back to the
        // expired-session route.
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  // Server outage: api.js confirms an unreachable server with a /health ping before firing
  // this event. Route to the full-page ServerDownScreen, which retries on its own and
  // reloads the app once the server answers again.
  useEffect(() => {
    const handleServerDown = () => setServerDown(true);
    window.addEventListener('clinic:server-unreachable', handleServerDown);
    return () => window.removeEventListener('clinic:server-unreachable', handleServerDown);
  }, []);

  // Listen for permission changes — re-fetch effective permissions immediately
  useEffect(() => {
    const handlePermChange = () => {
      if (authUser) {
        fetchRolePermissions(authUser.role, authUser.id);
      }
    };
    window.addEventListener('permissions:changed', handlePermChange);
    return () => window.removeEventListener('permissions:changed', handlePermChange);
  }, [authUser, fetchRolePermissions]);

  // Fetch and watch clinical settings (nurse.operational). Re-fetched on auth + when the
  // Clinical Settings page dispatches 'clinical-settings:changed' after a toggle save.
  const fetchClinicalSettings = useCallback(async () => {
    try {
      const data = await clinicalSettingsAPI.get();
      setNurseStationInUse(data?.nurseStationInUse !== false);
      setAllowSaveTemplateFromPrescription(data?.allowSaveTemplateFromPrescription === true);
    } catch {
      // Fail-open: assume the Nurse Station is in use so the sidebar entry isn't hidden by a
      // transient settings-fetch failure.
      setNurseStationInUse(true);
      setAllowSaveTemplateFromPrescription(false);
    }
  }, []);

  useEffect(() => {
    if (authUser) fetchClinicalSettings();
  }, [authUser, fetchClinicalSettings]);

  useEffect(() => {
    const handler = () => fetchClinicalSettings();
    window.addEventListener('clinical-settings:changed', handler);
    return () => window.removeEventListener('clinical-settings:changed', handler);
  }, [fetchClinicalSettings]);

  // Fetch the active clinic timezone (default location's zone) so dates/times across the app
  // render in clinic time regardless of the viewer's browser timezone. Re-fetched when the
  // Timezone Settings page changes the default ('clinic:timezone-changed').
  const fetchClinicTimezone = useCallback(async () => {
    try {
      const data = await locationAPI.getTimezone();
      if (data?.timezone) setClinicTimezone(data.timezone);
    } catch {
      // Fail-soft: the util defaults to Asia/Karachi, correct for this clinic.
    }
  }, []);

  useEffect(() => {
    if (authUser) fetchClinicTimezone();
  }, [authUser, fetchClinicTimezone]);

  useEffect(() => {
    const handler = () => fetchClinicTimezone();
    window.addEventListener('clinic:timezone-changed', handler);
    return () => window.removeEventListener('clinic:timezone-changed', handler);
  }, [fetchClinicTimezone]);

  // Admin "task" badge — number of soft-deleted documents awaiting review. Only fetched for
  // admins; refreshed whenever the review page or a delete dispatches 'clinic:deleted-docs-changed'.
  const [deletedDocsCount, setDeletedDocsCount] = useState(0);
  const refreshDeletedDocsCount = useCallback(async () => {
    if (authUser?.role !== 'ADMIN') { setDeletedDocsCount(0); return; }
    try {
      const s = await deletedDocumentAPI.getStats();
      setDeletedDocsCount(s?.count || 0);
    } catch {
      /* non-fatal */
    }
  }, [authUser?.role]);
  useEffect(() => {
    refreshDeletedDocsCount();
    const h = () => refreshDeletedDocsCount();
    window.addEventListener('clinic:deleted-docs-changed', h);
    return () => window.removeEventListener('clinic:deleted-docs-changed', h);
  }, [refreshDeletedDocsCount]);

  const handleLoginSuccess = (user, token) => {
    setAuthUser(user);
    setAuthToken(token);
    fetchRolePermissions(user.role, user.id);
    // Tell UIProvider to clear any sticky "Session expired" toast left over from before
    // the user logged back in.
    window.dispatchEvent(new Event('auth:login'));
  };

  const handleLogout = () => {
    localStorage.removeItem('clinic_auth_token');
    localStorage.removeItem('clinic_auth_user');
    setAuthUser(null);
    setAuthToken(null);
    setRolePermissions(null);
    setPermissionsSettled(false);
    setActiveModule('dashboard');
  };

  // Open Display Board in a new window
  const openDisplayBoard = useCallback(() => {
    // Check if window is already open
    if (displayBoardWindow && !displayBoardWindow.closed) {
      displayBoardWindow.focus();
      return;
    }

    // Calculate screen dimensions for fullscreen popup
    const width = window.screen.availWidth;
    const height = window.screen.availHeight;

    // Open the standalone display board HTML file with cache buster
    const cacheBuster = Date.now();
    const newWindow = window.open(
      `/display-board.html?v=${cacheBuster}`,
      'DisplayBoard',
      `width=${width},height=${height},left=0,top=0,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=no`
    );

    if (newWindow) {
      setDisplayBoardWindow(newWindow);
    } else {
      alert('Please allow popups for this site to open the Display Board');
    }
  }, [displayBoardWindow]);

  // Display Board is a standalone HTML page that opens in a new window, NOT an in-app
  // module. The sidebar click triggers openDisplayBoard() and never touches activeModule.
  // But the route can still slip in via refresh / bookmark / browser history / an older
  // bundle that did setActiveModule('display-board'). When that happens, the previous
  // behaviour was to fall through to the generic "Coming Soon" page — confusing. Now we
  // detect the case, open the standalone window, and snap activeModule to the sibling
  // (Token Queue) so the user lands on something real.
  useEffect(() => {
    if (authUser && activeModule === 'display-board') {
      openDisplayBoard();
      setActiveModule('token-queue');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModule, authUser]);

  // Toggle submenu expansion. Accordion: opening one closes whatever else was open, so the
  // sidebar never grows into a wall of nested items you have to scroll past to reach the next
  // section. Kept as an array (rather than a single id) because every read site tests
  // `.includes`, and because it lets "nothing is open" stay representable.
  const toggleSubmenu = (menuId) => {
    setExpandedMenus(prev => (prev.includes(menuId) ? [] : [menuId]));
  };

  // Check if current user has VIEW permission for a menu item
  const hasViewPermission = useCallback((menuId) => {
    // If permissions haven't loaded yet, show everything (fail-open during load)
    if (!rolePermissions) return true;
    const module = MENU_TO_PERMISSION_MODULE[menuId];
    // Items without a mapping (future/coming soon) are always visible
    if (!module) return true;
    const modulePerms = rolePermissions[module];
    // If module has no permission entry at all, deny access (fail-closed for known modules)
    if (!modulePerms) return false;
    // For items that share a module (e.g. USER_MANAGEMENT's Users and Roles tabs), the
    // per-menu action override pins the visibility check to that specific sub-action so each
    // tab can be granted independently while still rolling up under one module toggle.
    //
    // A sub-action REFINES the module, it does not bypass it: both must be granted. Checking the
    // sub-action alone let a role with the module revoked keep the item, because new sub-actions
    // seed from the role defaults rather than from whatever an admin has since switched off — so
    // adding a sub-action to a module handed the item back to everyone it had been taken from.
    const action = MENU_TO_PERMISSION_ACTION[menuId];
    if (!action) return modulePerms.VIEW === true;
    return modulePerms.VIEW === true && modulePerms[action] === true;
  }, [rolePermissions]);

  // Navigation menu items with submenus
  // Complaints assigned to this person. Declared here rather than with the other complaint
  // state further down because the menu array below reads it, and a const cannot be used
  // before it is declared.
  const [myComplaintsCount, setMyComplaintsCount] = useState(0);
  // Orders shared for review that involve this person: { toReview, toDecide, involved }. Declared
  // here for the same reason: the menu below reads it.
  const [orderReviewStats, setOrderReviewStats] = useState({ toReview: 0, toDecide: 0, involved: 0 });
  // Guides that have changed since this person last read them. Read from localStorage, not the
  // server, so it costs nothing; the Help Centre fires 'clinic:help-seen' when one is opened.
  const [updatedGuides, setUpdatedGuides] = useState([]);
  useEffect(() => {
    const refresh = () => setUpdatedGuides(updatedGuideIds(authUser?.id));
    refresh();
    window.addEventListener('clinic:help-seen', refresh);
    return () => window.removeEventListener('clinic:help-seen', refresh);
  }, [authUser?.id]);

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: <LayoutDashboard className="w-5 h-5" />,
      component: Dashboard,
      active: true
    },
    {
      id: 'patients',
      label: 'Patient',
      icon: <Users className="w-5 h-5" />,
      component: PatientManagement,
      active: true
    },
    // Nurse Station — top-level menu item, controlled by the NURSE_STATION permission
    // module (separate from QUEUE_MANAGEMENT so roles can be granted nurse-only or
    // queue-only access independently). Activity icon = the vitals-waveform glyph,
    // which is what the nurse station is actually for (BP, pulse, temp etc.).
    {
      id: 'nurse-station',
      label: 'Nurse Station',
      icon: <Activity className="w-5 h-5" />,
      component: NurseStation,
      active: true
    },
    {
      id: 'queue',
      label: 'Queue Management',
      icon: <Ticket className="w-5 h-5" />,
      active: true,
      hasSubmenu: true,
      submenu: [
        {
          id: 'display-board',
          label: 'Display Board',
          icon: <Monitor className="w-4 h-4" />,
          component: null,
          active: true,  // Now active!
          opensInNewWindow: true  // Flag to open in new window
        },
        {
          id: 'token-queue',
          label: 'Token Queue',
          icon: <ListOrdered className="w-4 h-4" />,
          component: TokenManagement,
          active: true
        }
      ]
    },
    // Prescription Module - Active (Doctor's main workflow)
    {
      id: 'prescriptions',
      label: 'Prescription',
      icon: <Stethoscope className="w-5 h-5" />,
      component: PrescriptionManagement,
      active: true
    },
    {
      id: 'family',
      label: 'Family Management',
      icon: <UserCog className="w-5 h-5" />,
      component: FamilyManagement,
      active: true
    },
    // Future modules - coming soon
    {
      id: 'appointments',
      label: 'Appointments',
      icon: <CalendarDays className="w-5 h-5" />,
      active: false
    },
    {
      id: 'invoice',
      label: 'Invoice',
      icon: <Receipt className="w-5 h-5" />,
      component: InvoiceManagement,
      active: true
    },
    {
      id: 'expenses',
      label: 'Expenses',
      icon: <Wallet className="w-5 h-5" />,
      active: true,
      hasSubmenu: true,
      submenu: [
        { id: 'expense-cash-position', label: 'Cash Position', component: ExpenseManagement, active: true, defaultTab: 'cash-position' },
        { id: 'expense-categories', label: 'Categories', component: ExpenseManagement, active: true, defaultTab: 'categories' },
        { id: 'expense-cheques', label: 'Cheques', component: ExpenseManagement, active: true, defaultTab: 'cheques' },
        { id: 'expense-dashboard', label: 'Dashboard', component: ExpenseManagement, active: true, defaultTab: 'dashboard' },
        { id: 'expense-entries', label: 'Entries', component: ExpenseManagement, active: true, defaultTab: 'entries' },
        { id: 'expense-monthly-report', label: 'Monthly Report', component: ExpenseManagement, active: true, defaultTab: 'monthly' },
        { id: 'expense-notes', label: 'Notes', component: ExpenseManagement, active: true, defaultTab: 'notes' },
      ]
    },
    {
      id: 'attendance',
      label: 'Attendance',
      icon: <Clock className="w-5 h-5" />,
      active: true,
      hasSubmenu: true,
      submenu: [
        { id: 'attendance-board', label: 'Board', component: AttendanceBoard, active: true },
        { id: 'attendance-mine', label: 'My Attendance', component: MyAttendance, active: true },
      ]
    },
    {
      id: 'treatment',
      label: 'Treatment Plan',
      icon: <ClipboardList className="w-5 h-5" />,
      active: false
    },
    {
      id: 'inpatient',
      label: 'In Patient',
      icon: <UserCheck className="w-5 h-5" />,
      active: false
    },
    {
      id: 'labtests',
      label: 'Lab Test Orders',
      icon: <FlaskConical className="w-5 h-5" />,
      active: false
    },
    {
      id: 'inventory',
      label: 'Inventory',
      icon: <Package className="w-5 h-5" />,
      active: true,
      hasSubmenu: true,
      // Intentional workflow order (NOT alphabetical): Medicines → Stock Receipts → Medicine
      // Orders → Assets → Vendors, per the user's request. Do not auto-sort this submenu.
      submenu: [
        {
          id: 'medicines',
          label: 'Medicines',
          icon: <Pill className="w-4 h-4" />,
          component: MedicinesManagement,
          active: true
        },
        {
          // Consumables (syringes, gloves, test strips). Same page, same catalogue table, filtered
          // to the SUPPLY category — a supply is stocked, batched, expiry-tracked, received and
          // billed exactly like a medicine, it is just not prescribed.
          id: 'medical-supplies',
          label: 'Medical Supplies',
          icon: <Syringe className="w-4 h-4" />,
          component: MedicinesManagement,
          active: true
        },
        {
          id: 'stock-receipts',
          label: 'Stock Receipts',
          icon: <PackagePlus className="w-4 h-4" />,
          component: StockReceiptsManagement,
          active: true
        },
        {
          id: 'medicine-orders',
          label: 'Medicine Orders',
          icon: <ShoppingCart className="w-4 h-4" />,
          component: MedicineOrdersManagement,
          active: true
        },
        // Orders a colleague is asked to check before they go to the supplier, and orders coming
        // back to whoever raised them. Deliberately NOT mapped in MENU_TO_PERMISSION_MODULE: the
        // reviewer needs no Medicine Orders permission, so it shows to anybody with a review of
        // their own, and the server decides what each person may see and do.
        {
          id: 'order-reviews',
          label: 'Order Reviews',
          icon: <ClipboardCheck className="w-4 h-4" />,
          component: OrderReviewsManagement,
          active: orderReviewStats.involved > 0
        },
        {
          id: 'assets',
          label: 'Assets & Equipment',
          icon: <Wrench className="w-4 h-4" />,
          component: AssetsManagement,
          active: true
        },
        {
          id: 'vendors',
          label: 'Vendors',
          icon: <Truck className="w-4 h-4" />,
          component: VendorsManagement,
          active: true
        }
      ]
    },
    // User Management sits down here with the other administration pages rather than up among
    // the day-to-day clinical ones: staff accounts are set up once, not worked with daily.
    {
      id: 'users',
      label: 'User Management',
      icon: <UserCheck className="w-5 h-5" />,
      active: true,
      hasSubmenu: true,
      submenu: [
        // Users sits above Roles & Permissions even though it breaks alphabetical order:
        // Users is the primary action of this module (the day-to-day list of staff), and
        // Roles & Permissions is the configuration surface — same gateway-first treatment
        // we apply to "Dashboard" in other submenus.
        {
          id: 'user-list',
          label: 'Users',
          icon: <Users className="w-4 h-4" />,
          component: UserManagement,
          active: true
        },
        {
          id: 'roles-permissions',
          label: 'Roles & Permissions',
          icon: <Shield className="w-4 h-4" />,
          component: RolesPermissions,
          active: true
        }
      ]
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: <BarChart3 className="w-5 h-5" />,
      active: true,
      hasSubmenu: true,
      // One shell component; the sidebar entry pins the report group via defaultTab,
      // the same pattern Expenses uses. Sorted alphabetically by label.
      submenu: [
        { id: 'reports-attendance', label: 'Attendance', component: ReportsManagement, active: true, defaultTab: 'attendance' },
        { id: 'reports-clinical', label: 'Clinical', component: ReportsManagement, active: true, defaultTab: 'clinical' },
        { id: 'reports-finance', label: 'Finance', component: ReportsManagement, active: true, defaultTab: 'finance' },
        { id: 'reports-inventory', label: 'Inventory', component: ReportsManagement, active: true, defaultTab: 'inventory' },
        { id: 'reports-opd', label: 'OPD & Patients', component: ReportsManagement, active: true, defaultTab: 'opd' },
      ]
    },
    {
      id: 'diagnostics',
      label: 'Diagnostics',
      icon: <Stethoscope className="w-5 h-5" />,
      active: false
    },
    {
      id: 'tasks',
      label: 'Tasks',
      icon: <CheckSquare className="w-5 h-5" />,
      active: false
    },
    {
      id: 'properties',
      label: 'Custom Properties',
      icon: <Zap className="w-5 h-5" />,
      active: false
    },
    {
      id: 'tools',
      label: 'Tools',
      icon: <Settings className="w-5 h-5" />,
      active: false
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: <Settings className="w-5 h-5" />,
      active: true,
      hasSubmenu: true,
      submenu: [
        {
          id: 'attendance-settings',
          label: 'Attendance',
          icon: <Clock className="w-4 h-4" />,
          component: AttendanceSettings,
          active: true
        },
        {
          id: 'branding-settings',
          label: 'Branding',
          icon: <ImageIcon className="w-4 h-4" />,
          component: BrandingSettings,
          active: true
        },
        {
          id: 'clinical-settings',
          label: 'Clinical Settings',
          icon: <StethoscopeIcon className="w-4 h-4" />,
          component: ClinicalSettingsManagement,
          active: true
        },
        {
          id: 'doctor-settings',
          label: 'Doctor Settings',
          icon: <UserCog className="w-4 h-4" />,
          component: DoctorSettingsManagement,
          active: true
        },
        {
          id: 'intake-questions',
          label: 'Intake Questions',
          icon: <ClipboardList className="w-4 h-4" />,
          component: IntakeQuestionsManagement,
          active: true
        },
        {
          id: 'medical-services',
          label: 'Medical Services',
          icon: <Briefcase className="w-4 h-4" />,
          component: MedicalServicesManagement,
          active: true
        },
        {
          id: 'patient-instructions',
          label: 'Patient Instructions',
          icon: <MessageSquare className="w-4 h-4" />,
          component: PatientInstructionsManagement,
          active: true
        },
        {
          id: 'specialities',
          label: 'Specialities',
          icon: <Award className="w-4 h-4" />,
          component: SpecialitiesManagement,
          active: true
        },
        {
          id: 'tags',
          label: 'Tags',
          icon: <TagIcon className="w-4 h-4" />,
          component: TagsManagement,
          active: true
        },
        {
          id: 'timezone-settings',
          label: 'Timezone Settings',
          icon: <Clock className="w-4 h-4" />,
          component: TimezoneSettings,
          active: true
        }
      ]
    },
    // Complaints sent by patients from the QR poster on the wall. Its own permission module, so it
    // can be handed to somebody other than an administrator later without giving them anything else.
    {
      id: 'complaints',
      label: 'Complaints',
      icon: <MessageSquareWarning className="w-5 h-5" />,
      component: ComplaintsManagement,
      active: true
    },
    // Somebody asked to look into one complaint sees it here, and nothing else. Deliberately NOT
    // mapped in MENU_TO_PERMISSION_MODULE: the whole point is that it reaches people who do not
    // hold COMPLAINTS. It appears only while something is actually assigned to them, and the
    // server decides what the page may load.
    {
      id: 'my-complaints',
      label: 'My Complaints',
      icon: <MessageSquareWarning className="w-5 h-5" />,
      component: ComplaintsManagement,
      defaultTab: 'mine',
      active: myComplaintsCount > 0
    },
    // Deleted Documents — admin-only review queue for documents staff soft-deleted, plus the disk
    // cleanup tool. No permission module maps to it; gated by `adminOnly` like What's New.
    {
      id: 'deleted-documents',
      label: 'Deleted Documents',
      icon: <FileX className="w-5 h-5" />,
      component: DeletedDocumentsManagement,
      active: true,
      adminOnly: true
    },
    {
      id: 'deleted-patients',
      label: 'Deleted Patients',
      icon: <UserX className="w-5 h-5" />,
      component: DeletedPatientsManagement,
      active: true,
      adminOnly: true
    },
    // What's New — admin-only (release notes are an internal/ops concern). No permission
    // module maps to it, so it's gated by the `adminOnly` flag below. Update
    // src/data/releaseNotes.js with each deploy.
    {
      id: 'whats-new',
      label: "What's New",
      icon: <ScrollText className="w-5 h-5" />,
      component: ReleaseNotes,
      active: true,
      adminOnly: true
    },
    // User Guides — deliberately ungated. No permission module maps to it and there is no
    // adminOnly flag, because everybody may read every guide (owner, 2026-10-05). It sits last so
    // it is in the same place on every role's sidebar.
    {
      id: 'help',
      label: 'User Guides',
      icon: <BookOpen className="w-5 h-5" />,
      component: HelpCenter,
      active: true
    }
  ];

  // Apply permission-based filtering to menu items
  const filteredMenuItems = menuItems.map(item => {
    if (item.hasSubmenu) {
      // Filter submenu items by VIEW permission, drop "coming soon" (inactive) entries, and
      // drop anything hidden behind a parked feature flag (see config/features.js).
      const filteredSub = item.submenu.filter(
        sub => sub.active && hasViewPermission(sub.id) && !HIDDEN_MENU_IDS.includes(sub.id),
      );
      // Container parents have no page of their own — they only hold children. If no
      // submenu item is visible, hide the whole parent (an empty container that does
      // nothing on click shouldn't appear, even though the parent id itself has no
      // permission mapping and would otherwise read as "always visible").
      if (filteredSub.length === 0) {
        return { ...item, active: false, hasSubmenu: false, _hidden: true };
      }
      return { ...item, submenu: filteredSub, active: filteredSub.length > 0 };
    }
    // Hide top-level items parked behind a feature flag (see config/features.js).
    if (HIDDEN_MENU_IDS.includes(item.id)) {
      return { ...item, _hidden: true };
    }
    // Admin-only items (e.g. What's New) have no permission module — gate them on role.
    if (item.adminOnly && authUser?.role !== 'ADMIN') {
      return { ...item, _hidden: true };
    }
    // For non-submenu active items, check permission
    if (item.active && !hasViewPermission(item.id)) {
      return { ...item, active: false, _hidden: true };
    }
    // Hide the Nurse Station entry when no doctor uses a nurse — the queue would always be empty
    // and every doctor records vitals at Rx start instead.
    if (item.id === 'nurse-station' && !nurseStationInUse) {
      return { ...item, active: false, _hidden: true };
    }
    // Hide "coming soon" top-level items (inactive, no submenu) entirely.
    if (!item.active && !item.hasSubmenu) {
      return { ...item, _hidden: true };
    }
    return item;
  }).filter(item => !item._hidden);

  // On mount: auto-expand parent submenus if hash points to a child item
  useEffect(() => {
    if (autoExpandedRef.current) return;
    autoExpandedRef.current = true;
    const mod = getModuleFromHash();
    for (const item of filteredMenuItems) {
      if (item.hasSubmenu && item.submenu?.some(s => s.id === mod)) {
        // Replaces rather than appends, so a deep link opens exactly one section.
        setExpandedMenus([item.id]);
        break;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredMenuItems]);

  // Find component by module ID (handles submenus)
  const findComponent = (moduleId) => {
    // Check main menu items
    for (const item of filteredMenuItems) {
      if (item.id === moduleId && item.component) {
        return { component: item.component, label: item.label, active: item.active };
      }
      // Check submenus
      if (item.submenu) {
        const subItem = item.submenu.find(sub => sub.id === moduleId);
        if (subItem) {
          return {
            component: subItem.component,
            label: subItem.label,
            active: subItem.active,
            parentLabel: item.label,
            defaultTab: subItem.defaultTab,
          };
        }
      }
    }
    // Return info for inactive module
    const item = filteredMenuItems.find(i => i.id === moduleId);
    return { component: null, label: item?.label || 'Unknown', active: false };
  };

  // The first page in the menu this user can open: where they go when the current one is not theirs.
  const firstOpenableModule = () => {
    for (const item of filteredMenuItems) {
      if (item.hasSubmenu) {
        const sub = item.submenu?.find(s => s.active && !s.opensInNewWindow && s.component);
        if (sub) return sub.id;
      } else if (item.active && item.component) {
        return item.id;
      }
    }
    return null;
  };

  const renderModule = () => {
    // No page mounts before the user's permissions are in, nor one they cannot open while the
    // redirect below moves them on. Either way the page would fetch its data first: every sign-in
    // without the dashboard asked the server for it, which refuses (prod's log, 2026-10-12).
    if (!permissionsSettled) return null;
    if (!hasViewPermission(activeModule)) {
      if (firstOpenableModule()) return null;
      return (
        <div className="p-6">
          <div className="max-w-xl mx-auto bg-white border border-gray-200 rounded-xl p-8 text-center">
            <h2 className="text-lg font-semibold text-gray-900">No pages to show</h2>
            <p className="mt-2 text-sm text-gray-600">
              Your account cannot open any page yet. Ask an administrator to check your permissions.
            </p>
          </div>
        </div>
      );
    }

    const moduleInfo = findComponent(activeModule);

    if (moduleInfo.component && moduleInfo.active) {
      const Component = moduleInfo.component;

      // One component serves both catalogues; the category decides which it shows.
      if (activeModule === 'medicines' || activeModule === 'medical-supplies') {
        return <Component
          // Keyed on the route: the two catalogues are the same component, so without this React
          // keeps the mounted instance when you switch between them and only swaps the prop. The
          // list would then still be showing whichever catalogue loaded first, and the search box,
          // status filter and type filter would carry over — a Type of "Tablet" left over from
          // Medicines matches no supply at all.
          key={activeModule}
          hasPermission={hasPermission}
          category={activeModule === 'medical-supplies' ? 'SUPPLY' : 'MEDICINE'}
        />;
      }

      // Pass navigation functions to components
      if (activeModule === 'patients') {
        return <Component
          searchQuery={searchQuery}
          onNavigateToToken={() => setActiveModule('token-queue')}
          authUser={authUser}
          hasPermission={hasPermission}
        />;
      }

      if (activeModule === 'token-queue') {
        return <Component
          searchQuery={searchQuery}
          authUser={authUser}
          defaultTab={moduleInfo.defaultTab}
          onNavigateToPatients={() => setActiveModule('patients')}
          hasPermission={hasPermission}
        />;
      }

      return <Component
        searchQuery={searchQuery}
        authUser={authUser}
        defaultTab={moduleInfo.defaultTab}
        hasPermission={hasPermission}
        allowSaveTemplateFromPrescription={allowSaveTemplateFromPrescription}
      />;
    }

    // Coming Soon component for inactive modules
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Monitor className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {moduleInfo.label}
            </h2>
            <p className="text-gray-600 mb-8">
              This module is coming soon! We're working hard to bring you more features.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="font-semibold text-blue-900 mb-2">Available Modules</h3>
              <div className="flex flex-wrap justify-center gap-2">
                {filteredMenuItems.filter(item => item.active && !item.hasSubmenu).map(item => (
                  <button
                    key={item.id}
                    onClick={() => setActiveModule(item.id)}
                    className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm hover:bg-blue-200 transition-colors"
                  >
                    {item.label}
                  </button>
                ))}
                {/* Add active submenu items */}
                {filteredMenuItems.filter(item => item.hasSubmenu).map(item =>
                  item.submenu?.filter(sub => sub.active).map(sub => (
                    <button
                      key={sub.id}
                      onClick={() => setActiveModule(sub.id)}
                      className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm hover:bg-blue-200 transition-colors"
                    >
                      {sub.label}
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to patients module for search
      setActiveModule('patients');
    }
  };

  // Check if user has a specific permission (e.g. hasPermission('INVOICE', 'CREATE'))
  const hasPermission = useCallback((module, action) => {
    if (!rolePermissions) return true; // fail-open during load
    const modPerms = rolePermissions[module];
    if (!modPerms) return false;
    return modPerms[action] === true;
  }, [rolePermissions]);

  // Complaints patients sent from the poster on the wall. The count rides in the sidebar so an
  // unopened complaint is visible from every screen, and the first look of a browser session also
  // says it out loud, which is what "tell me when I log in" means in a web app. Placed after
  // hasPermission because it is granted by permission, not by role.
  const [complaintsCount, setComplaintsCount] = useState(0);
  // Items that will run out before any delivery can arrive, for whoever sends orders. Counted by
  // OrderDayReminder, which reads it anyway for its order-day nudge.
  const [orderTodayCount, setOrderTodayCount] = useState(0);
  const refreshComplaintsCount = useCallback(async () => {
    try {
      // Readable by anybody: the clinic-wide figures come back as 0 without COMPLAINTS.VIEW, while
      // `mine` is a count of the caller's own rows. Without that, the one person who needs the
      // My Complaints link — an assignee with no complaints permission — could never be shown it.
      const s = await complaintAPI.getStats();
      setMyComplaintsCount(s?.mine || 0);
      const count = s?.newCount || 0;
      setComplaintsCount(count);
      if (count > 0) {
        try {
          if (!sessionStorage.getItem('clinic_complaints_notified')) {
            sessionStorage.setItem('clinic_complaints_notified', '1');
            showToast('warning', count === 1
              ? 'A new complaint is waiting. Open Complaints to read it.'
              : `${count} new complaints are waiting. Open Complaints to read them.`);
          }
        } catch {
          /* Site data blocked: the badge still shows it. */
        }
      }
    } catch {
      /* non-fatal */
    }
  }, [showToast]);
  // Order reviews waiting on this person. Polled every two minutes and when the window comes back
  // into focus, since the other person's action is what changes it: the reviewer is told when an
  // order is shared with them, and whoever raised it when it comes back.
  const orderReviewSeen = React.useRef(null);
  const refreshOrderReviews = useCallback(async () => {
    try {
      const s = await orderReviewAPI.getStats();
      const next = { toReview: s?.toReview || 0, toDecide: s?.toDecide || 0, involved: s?.involved || 0 };
      setOrderReviewStats(next);
      let before = orderReviewSeen.current;
      if (!before) {
        try { before = JSON.parse(sessionStorage.getItem('clinic_order_reviews_seen') || 'null'); } catch { before = null; }
      }
      before = before || { toReview: 0, toDecide: 0 };
      if (next.toReview > before.toReview) {
        showToast('info', next.toReview === 1
          ? 'An order is waiting for your review. Open Inventory, Order Reviews.'
          : `${next.toReview} orders are waiting for your review. Open Inventory, Order Reviews.`);
      }
      if (next.toDecide > before.toDecide) {
        showToast('info', next.toDecide === 1
          ? 'An order you shared is back from review. Open Inventory, Order Reviews to accept it.'
          : `${next.toDecide} orders you shared are back from review. Open Inventory, Order Reviews.`);
      }
      orderReviewSeen.current = next;
      try { sessionStorage.setItem('clinic_order_reviews_seen', JSON.stringify(next)); } catch { /* the badge still shows it */ }
    } catch {
      /* non-fatal */
    }
  }, [showToast]);
  useEffect(() => {
    if (!rolePermissions) return undefined;
    refreshOrderReviews();
    const h = () => refreshOrderReviews();
    const onFocus = () => { if (document.visibilityState !== 'hidden') refreshOrderReviews(); };
    const timer = setInterval(h, 120000);
    window.addEventListener('clinic:order-reviews-changed', h);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener('clinic:order-reviews-changed', h);
      window.removeEventListener('focus', onFocus);
    };
  }, [rolePermissions, refreshOrderReviews]);

  useEffect(() => {
    if (!rolePermissions) return undefined;
    refreshComplaintsCount();
    const h = () => refreshComplaintsCount();
    window.addEventListener('clinic:complaints-changed', h);
    return () => window.removeEventListener('clinic:complaints-changed', h);
  }, [rolePermissions, refreshComplaintsCount]);

  // If the active module isn't accessible to this user (e.g. a Receptionist with no Dashboard
  // permission landing on the default 'dashboard'), redirect to the first menu they CAN open.
  // Dashboard is not special-cased — if they lack it, they're moved on like any other module.
  useEffect(() => {
    if (!rolePermissions) return; // permissions still loading — fail-open, don't redirect yet
    if (hasViewPermission(activeModule)) return; // current module is fine
    const fallback = firstOpenableModule();
    if (fallback && fallback !== activeModule) setActiveModule(fallback);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolePermissions, activeModule, hasViewPermission]);

  // Check if a submenu item is active
  const isSubmenuActive = (submenu) => {
    return submenu?.some(sub => sub.id === activeModule);
  };

  // Get current module label for header
  const getCurrentModuleLabel = () => {
    const moduleInfo = findComponent(activeModule);
    if (moduleInfo.parentLabel) {
      return `${moduleInfo.parentLabel} > ${moduleInfo.label}`;
    }
    return moduleInfo.label;
  };

  // Server unreachable — takes precedence over everything (including login): no part of
  // the app is usable without the backend, so show one graceful page instead of scattered
  // per-module failures.
  if (serverDown) {
    return <ServerDownScreen />;
  }

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-navy-800 via-navy-900 to-navy-950">
        <div className="text-center">
          <ClinicLogo size={64} variant="white" />
          <p className="text-navy-200 mt-4 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!authUser || !authToken) {
    return (
      <>
        {isQa && <EnvironmentStrip />}
        <Login onLoginSuccess={handleLoginSuccess} />
      </>
    );
  }

  // Collapsed "icon rail" only applies on desktop; the mobile drawer is always full + labelled.
  const navCollapsed = sidebarCollapsed && isDesktop;

  // Only the people who work a cash drawer see it in the top bar (Cash Drawers > "Works a cash
  // drawer", owner 2026-10-02), not everyone who could issue a token, such as a doctor. Anyone whose
  // drawer is open still sees it, so it can be counted and closed.
  const takesMoney = hasPermission('CASH_DRAWERS', 'OPERATE_DRAWER');

  return (
    <CashDrawerProvider>
    <div className={`flex h-screen bg-gray-100 ${isQa ? ENVIRONMENT_STRIP_OFFSET : ''}`}>
      {isQa && <EnvironmentStrip />}
      {/* Offers a reload when a deploy lands while this tab is open. Only rendered once signed in,
          because the login screen forces the reload itself. */}
      <NewVersionBanner />

      {/* Mobile nav backdrop — tap to close the drawer (below lg only). */}
      {mobileNavOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setMobileNavOpen(false)}
          aria-hidden="true"
        />
      )}
      {/* Sidebar — persistent rail on desktop, slide-over drawer on mobile. */}
      {/* Navy, the logo's own frame colour (2026-10-01 retheme). The logo itself sits on a white
          tile here (ClinicLogo variant "white"), or its navy parts would vanish. */}
      <div className={`bg-navy-900 text-white shadow-xl transition-transform duration-300 flex flex-col
        fixed inset-y-0 left-0 z-50 w-64 transform ${isQa ? ENVIRONMENT_STRIP_TOP : ''}
        ${mobileNavOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:static lg:z-auto lg:translate-x-0 lg:transition-all ${sidebarCollapsed ? 'lg:w-16' : 'lg:w-64'}`}>

        {/* Sidebar Header */}
        <div className="p-4 border-b border-navy-800">
          <div className="flex items-center justify-between">
            {!navCollapsed ? (
              <ClinicLogo size={34} showText variant="white" />
            ) : (
              <ClinicLogo size={24} variant="white" />
            )}
            <button
              onClick={() => (isDesktop ? setSidebarCollapsed(!sidebarCollapsed) : setMobileNavOpen(false))}
              className="p-1 text-navy-200 hover:text-white hover:bg-white/10 rounded-md transition-colors"
              aria-label={isDesktop ? 'Toggle sidebar' : 'Close menu'}
            >
              {navCollapsed ? <Menu className="w-4 h-4" /> : <X className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {!navCollapsed && (
          <div className="p-4 border-b border-navy-800">
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                placeholder="Search patients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-4 py-2 text-sm bg-navy-800 text-white placeholder-navy-300 border border-navy-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-gold-400"
              />
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-navy-300" />
            </form>
          </div>
        )}

        {/* Navigation Menu */}
        <div className="flex-1 overflow-y-auto py-4">
          <nav className="space-y-1 px-2">
            {filteredMenuItems.map((item) => (
              <div key={item.id}>
                {/* Main menu item */}
                <button
                  onClick={() => {
                    if (item.hasSubmenu) {
                      toggleSubmenu(item.id);
                      // If clicking on parent, snap to the first active submenu — but
                      // skip submenu items that just trigger a side-effect (e.g. Display
                      // Board opens in a new window). Otherwise expanding Queue Management
                      // would open the Display Board popup, which isn't what the user
                      // intended by clicking the parent.
                      if (!expandedMenus.includes(item.id)) {
                        const firstActiveSubmenu = item.submenu?.find(
                          sub => sub.active && !sub.opensInNewWindow,
                        );
                        if (firstActiveSubmenu) {
                          setActiveModule(firstActiveSubmenu.id);
                        }
                      }
                    } else if (item.active) {
                      setActiveModule(item.id);
                      setMobileNavOpen(false); // navigating closes the mobile drawer
                    }
                  }}
                  disabled={!item.active && !item.hasSubmenu}
                  className={`
                    w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 border-l-4
                    ${(activeModule === item.id || isSubmenuActive(item.submenu)) && item.active
                      ? 'bg-white/10 text-white border-gold-400'
                      : item.active || item.hasSubmenu
                        ? 'text-navy-100 border-transparent hover:bg-white/5 hover:text-white'
                        : 'text-navy-400 border-transparent cursor-not-allowed'
                    }
                    ${navCollapsed ? 'justify-center px-2' : ''}
                  `}
                  title={navCollapsed ? item.label : ''}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`${(activeModule === item.id || isSubmenuActive(item.submenu)) && item.active ? 'text-gold-400' : 'text-navy-300'}`}>
                      {item.icon}
                    </div>
                    {!navCollapsed && (
                      <span className="truncate">{item.label}</span>
                    )}
                  </div>

                  {!navCollapsed && item.hasSubmenu && (
                    expandedMenus.includes(item.id)
                      ? <ChevronDown className="w-4 h-4 text-navy-300" />
                      : <ChevronRight className="w-4 h-4 text-navy-300" />
                  )}

                  {!navCollapsed && !item.active && !item.hasSubmenu && (
                    <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                      Soon
                    </span>
                  )}
                  {!navCollapsed && item.id === 'deleted-documents' && deletedDocsCount > 0 && (
                    <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full font-semibold">
                      {deletedDocsCount}
                    </span>
                  )}
                  {!navCollapsed && item.id === 'inventory' && orderTodayCount > 0 && !expandedMenus.includes('inventory') && (
                    <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full font-semibold">
                      {orderTodayCount}
                    </span>
                  )}
                  {!navCollapsed && item.id === 'inventory' && orderReviewStats.toReview + orderReviewStats.toDecide > 0 && !expandedMenus.includes('inventory') && (
                    <span title="Order reviews waiting on you" className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full font-semibold">
                      {orderReviewStats.toReview + orderReviewStats.toDecide}
                    </span>
                  )}
                  {!navCollapsed && item.id === 'complaints' && complaintsCount > 0 && (
                    <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full font-semibold">
                      {complaintsCount}
                    </span>
                  )}
                  {!navCollapsed && item.id === 'my-complaints' && myComplaintsCount > 0 && (
                    <span className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full font-semibold">
                      {myComplaintsCount}
                    </span>
                  )}
                  {/* A guide somebody has already read is not the one they read. The count is of
                      guides, not of changes, because that is what they will go and look at. */}
                  {!navCollapsed && item.id === 'help' && updatedGuides.length > 0 && (
                    <span
                      title={`${updatedGuides.length} guide${updatedGuides.length === 1 ? ' has' : 's have'} a new version`}
                      className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full font-semibold animate-pulse"
                    >
                      {updatedGuides.length}
                    </span>
                  )}
                </button>

                {/* Submenu items */}
                {item.hasSubmenu && expandedMenus.includes(item.id) && !navCollapsed && (
                  <div className="ml-4 mt-1 space-y-1 border-l-2 border-navy-700 pl-3">
                    {item.submenu.map((subItem) => (
                      <button
                        key={subItem.id}
                        onClick={() => {
                          if (subItem.active) {
                            if (subItem.opensInNewWindow) {
                              openDisplayBoard();
                            } else {
                              setActiveModule(subItem.id);
                              setMobileNavOpen(false); // navigating closes the mobile drawer
                            }
                          }
                        }}
                        disabled={!subItem.active}
                        className={`
                          w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-all duration-200
                          ${activeModule === subItem.id && subItem.active && !subItem.opensInNewWindow
                            ? 'bg-white/10 text-white font-medium border-r-4 border-gold-400'
                            : subItem.active
                              ? 'text-navy-200 hover:bg-white/5 hover:text-white'
                              : 'text-navy-400 cursor-not-allowed'
                          }
                        `}
                      >
                        <div className="flex items-center space-x-2">
                          <div className={`${activeModule === subItem.id && subItem.active && !subItem.opensInNewWindow ? 'text-gold-400' : 'text-navy-300'}`}>
                            {subItem.icon}
                          </div>
                          <span>{subItem.label}</span>
                        </div>

                        {subItem.id === 'order-reviews' && orderReviewStats.toReview + orderReviewStats.toDecide > 0 && (
                          <span title={[
                            orderReviewStats.toReview ? `${orderReviewStats.toReview} for you to review` : null,
                            orderReviewStats.toDecide ? `${orderReviewStats.toDecide} back to you` : null,
                          ].filter(Boolean).join(', ')}
                            className="text-xs bg-amber-500 text-white px-2 py-0.5 rounded-full font-semibold">
                            {orderReviewStats.toReview + orderReviewStats.toDecide}
                          </span>
                        )}

                        {subItem.id === 'medicine-orders' && orderTodayCount > 0 && (
                          <span title={`${orderTodayCount} item${orderTodayCount === 1 ? '' : 's'} will run out before any delivery`}
                            className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full font-semibold">
                            {orderTodayCount}
                          </span>
                        )}

                        {subItem.opensInNewWindow && subItem.active && (
                          <ExternalLink className="w-3 h-3 text-navy-300" />
                        )}

                        {!subItem.active && (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                            Soon
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="relative bg-white shadow-sm px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-3">
            {/* Left side - Hospital Info */}
            <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
              {/* Hamburger — opens the slide-over nav (mobile only). */}
              <button
                onClick={() => setMobileNavOpen(true)}
                className="lg:hidden p-2 -ml-1 hover:bg-gray-100 rounded-lg flex-shrink-0"
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6 text-gray-700" />
              </button>
              <ClinicLogo size={44} variant="color" className="hidden sm:block flex-shrink-0" />
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate" style={{fontFamily: 'Crimson Text, serif'}}>
                  <span className="urdu-text text-navy-800">پروین اسلم میموریل ہسپتال</span>
                </h1>
                <p className="hidden sm:block text-sm text-gray-600 font-medium">
                  Perveen Aslam Memorial Hospital • Modern Healthcare Management
                </p>
              </div>
            </div>

            {/* Right side — current module + user menu */}
            <div className="flex items-center gap-3">
              {/* Current page indicator — the bare module name alone read as a stray word, so
                  it's now prefixed with a small "Current page" caption + a location pin so it's
                  unmistakably "where you are", not a button or status. */}
              <div className="hidden md:flex items-center gap-2 pr-3 border-r border-gray-200">
                <MapPin className="w-4 h-4 text-gold-600 flex-shrink-0" />
                <div className="leading-tight">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Current page</p>
                  <p className="text-sm font-semibold text-gray-800">{getCurrentModuleLabel()}</p>
                </div>
              </div>

              {/* The cashier's drawer: open it, or count and close it. */}
              <CashDrawerButton show={takesMoney} />

              {/* User Menu */}
              <ProfileMenu authUser={authUser} onLogout={handleLogout} onAuthUserUpdate={setAuthUser} theme={theme} onThemeChange={setTheme} colorTheme={colorTheme} onColorThemeChange={setColorTheme} />
            </div>
          </div>
          {/* The logo's own four colours, fixed whatever the colour theme (navy, gold, green, sky),
              the same band the printed prescription carries under its letterhead. */}
          <div className="absolute left-0 right-0 bottom-0 flex h-[3px]" aria-hidden="true">
            <span className="flex-1 bg-[#003078]" />
            <span className="flex-1 bg-[#c88800]" />
            <span className="flex-1 bg-[#009038]" />
            <span className="flex-1 bg-[#00a0c4]" />
          </div>
        </header>

        {/* Main Content */}
        <main ref={mainScrollRef} id="app-main" className="flex-1 overflow-auto">
          {/* Only the guides are lazy today; the fallback is what their chunk loads behind. */}
          <Suspense fallback={<div className="p-6 text-gray-500">Loading…</div>}>
            {renderModule()}
          </Suspense>
        </main>
      </div>

      {/* Two nudges about marking time, one at each end of the day. They sit in the shell rather
          than on the attendance page because the whole point is to reach somebody who is working
          elsewhere in the app: arriving and going straight to the queue, or about to walk out
          without marking their time. Only one of them can ever be due, since the first is for a day
          with no punch on it and the second for a shift still open. */}
      <PunchInReminder authUser={authUser} onOpenAttendance={() => setActiveModule('attendance-mine')} />
      <PunchOutReminder authUser={authUser} onOpenAttendance={() => setActiveModule('attendance-mine')} />
      {/* On a supplier's order day, whoever sends orders is told what to order. */}
      <OrderDayReminder
        authUser={authUser}
        canSend={!!authUser && rolePermissions != null && hasPermission('PHARMACY_ORDERS', 'SUBMIT_ORDER') && hasPermission('PHARMACY_ORDERS', 'VIEW')}
        onOpenOrders={() => setActiveModule('medicine-orders')}
        onCount={setOrderTodayCount}
      />
    </div>
    </CashDrawerProvider>
  );
}

export default App;